import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/contexts/LanguageContext';
import { loc } from '@/lib/localize';

interface TourDetail {
  id: string;
  title: string;
  slug: string;
  duration: string;
  spots: number;
  category: string;
  group_type: string;
  price: number;
  image: string;
  breadcrumb: string[];
  rating_score: number;
  rating_source: string;
  rating_count: number;
  licensed: boolean;
  different_link: string;
  tags: { icon: string; label: string }[];
  description: string;
  at_a_glance: Record<string, string>;
  route: { points: string[]; description: string };
  about_this_trip: string;
  includes: string[];
  excludes: string[];
  right_for_you: { goodIf: string[]; notIf: string[] };
  cancellation: { group: string[]; private: string[] };
}

interface TourDetailRaw extends TourDetail {
  title_vi: string | null;
  duration_vi: string | null;
  category_vi: string | null;
  group_type_vi: string | null;
  breadcrumb_vi: string[] | null;
  tags_vi: { icon: string; label: string }[] | null;
  description_vi: string | null;
  at_a_glance_vi: Record<string, string> | null;
  route_vi: { points: string[]; description: string } | null;
  about_this_trip_vi: string | null;
  includes_vi: string[] | null;
  excludes_vi: string[] | null;
  right_for_you_vi: { goodIf: string[]; notIf: string[] } | null;
  cancellation_vi: { group: string[]; private: string[] } | null;
}

interface TourImage {
  id: string;
  image_url: string;
  sort_order: number;
}

interface TourItinerary {
  id: string;
  day_number: number;
  title: string;
  subtitle: string | null;
  content: string | null;
  afternoon_title: string | null;
  afternoon_content: string | null;
  images: string[] | null;
  timeline: { time: string; title: string; description: string; image?: string }[] | null;
  sort_order: number;
}

interface TourItineraryRaw extends TourItinerary {
  title_vi: string | null;
  subtitle_vi: string | null;
  content_vi: string | null;
  afternoon_title_vi: string | null;
  afternoon_content_vi: string | null;
  timeline_vi: { time: string; title: string; description: string; image?: string }[] | null;
}

interface TourReview {
  id: string;
  author: string;
  avatar: string;
  initial?: string;
  rating: number;
  review_date: string;
  title: string;
  content: string;
  images: string[] | null;
}

interface TourReviewRaw extends TourReview {
  title_vi: string | null;
  content_vi: string | null;
}

interface TourFAQ {
  id: string;
  question: string;
  answer: string;
  sort_order: number;
}

interface TourFAQRaw extends TourFAQ {
  question_vi: string | null;
  answer_vi: string | null;
}

export function useTourDetail(tourId: string) {
  const [rawTour, setRawTour] = useState<TourDetailRaw | null>(null);
  const [images, setImages] = useState<TourImage[]>([]);
  const [rawItinerary, setRawItinerary] = useState<TourItineraryRaw[]>([]);
  const [rawReviews, setRawReviews] = useState<TourReviewRaw[]>([]);
  const [rawFaqs, setRawFaqs] = useState<TourFAQRaw[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      try {
        setLoading(true);
        setError(null);

        const [
          tourResult,
          imagesResult,
          itineraryResult,
          reviewsResult,
          faqsResult,
        ] = await Promise.all([
          supabase.from('vietnamtravel_tours').select('*').eq('id', tourId).maybeSingle(),
          supabase.from('vietnamtravel_tour_images').select('*').eq('tour_id', tourId).order('sort_order'),
          supabase.from('vietnamtravel_tour_itinerary').select('*').eq('tour_id', tourId).order('sort_order'),
          supabase.from('vietnamtravel_tour_reviews').select('*').eq('tour_id', tourId).order('created_at'),
          supabase.from('vietnamtravel_tour_faqs').select('*').eq('tour_id', tourId).order('sort_order'),
        ]);

        if (tourResult.error) throw tourResult.error;

        if (!cancelled) {
          setRawTour(tourResult.data as TourDetailRaw | null);
          setImages((imagesResult.data || []) as TourImage[]);
          setRawItinerary((itineraryResult.data || []) as TourItineraryRaw[]);
          setRawReviews((reviewsResult.data || []) as TourReviewRaw[]);
          setRawFaqs((faqsResult.data || []) as TourFAQRaw[]);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load tour detail');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (tourId) fetchAll();
    return () => { cancelled = true; };
  }, [tourId]);

  const tour: TourDetail | null = rawTour
    ? {
        id: rawTour.id,
        title: loc(language, rawTour.title, rawTour.title_vi),
        slug: rawTour.slug,
        duration: loc(language, rawTour.duration, rawTour.duration_vi),
        spots: rawTour.spots,
        category: loc(language, rawTour.category, rawTour.category_vi),
        group_type: loc(language, rawTour.group_type, rawTour.group_type_vi),
        price: rawTour.price,
        image: rawTour.image,
        breadcrumb: loc(language, rawTour.breadcrumb, rawTour.breadcrumb_vi),
        rating_score: rawTour.rating_score,
        rating_source: rawTour.rating_source,
        rating_count: rawTour.rating_count,
        licensed: rawTour.licensed,
        different_link: rawTour.different_link,
        tags: loc(language, rawTour.tags, rawTour.tags_vi),
        description: loc(language, rawTour.description, rawTour.description_vi),
        at_a_glance: loc(language, rawTour.at_a_glance, rawTour.at_a_glance_vi),
        route: loc(language, rawTour.route, rawTour.route_vi),
        about_this_trip: loc(language, rawTour.about_this_trip, rawTour.about_this_trip_vi),
        includes: loc(language, rawTour.includes, rawTour.includes_vi),
        excludes: loc(language, rawTour.excludes, rawTour.excludes_vi),
        right_for_you: loc(language, rawTour.right_for_you, rawTour.right_for_you_vi),
        cancellation: loc(language, rawTour.cancellation, rawTour.cancellation_vi),
      }
    : null;

  const itinerary: TourItinerary[] = rawItinerary.map((r) => ({
    id: r.id,
    day_number: r.day_number,
    title: loc(language, r.title, r.title_vi),
    subtitle: loc(language, r.subtitle, r.subtitle_vi),
    content: loc(language, r.content, r.content_vi),
    afternoon_title: loc(language, r.afternoon_title, r.afternoon_title_vi),
    afternoon_content: loc(language, r.afternoon_content, r.afternoon_content_vi),
    images: r.images,
    timeline: loc(language, r.timeline, r.timeline_vi),
    sort_order: r.sort_order,
  }));

  const reviews: TourReview[] = rawReviews.map((r) => ({
    id: r.id,
    author: r.author,
    avatar: r.avatar,
    initial: r.initial,
    rating: r.rating,
    review_date: r.review_date,
    title: loc(language, r.title, r.title_vi),
    content: loc(language, r.content, r.content_vi),
    images: r.images,
  }));

  const faqs: TourFAQ[] = rawFaqs.map((r) => ({
    id: r.id,
    question: loc(language, r.question, r.question_vi),
    answer: loc(language, r.answer, r.answer_vi),
    sort_order: r.sort_order,
  }));

  return { tour, images, itinerary, reviews, faqs, loading, error };
}